/* eslint-disable import/first */
import React, { lazy, Suspense } from 'react'
import { Route, Switch, Redirect, RouteComponentProps } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DefaultLayout } from './layouts'
import withTracker from './withTracker'
import './styles/app.scss'
import './styles/bootstrap.scss'
import './styles/Vendor'
import './main.scss'
import { IAppState } from '../src/store/state.interface.web'
import { getIsSuperUser } from '../src/auth/Login/login.selector'
import { IUser } from '../src/users/index.web'

const MessagesListView = lazy(() =>
  import('../src/message/index.web').then(module => ({ default: module.MessagesListView }))
)
const NotesListView = lazy(() =>
  import('../src/note/index.web').then(module => ({ default: module.NotesListView }))
)

const AddPatientView = lazy(() =>
  import('../src/patient/index.web').then(module => ({ default: module.AddPatientView }))
)
const PatientDetailView = lazy(() =>
  import('../src/patient/index.web').then(module => ({ default: module.PatientDetailView }))
)
const AdherenceListView = lazy(() =>
  import('../src/patient/index.web').then(module => ({ default: module.AdherenceListView }))
)
const EditPatientView = lazy(() =>
  import('../src/patient/index.web').then(module => ({ default: module.EditPatientView }))
)
const BillingReport = lazy(() =>
  import('../src/billing/index.web').then(module => ({ default: module.BillingReport }))
)
const BillingDetailedReport = lazy(() =>
  import('../src/billing/index.web').then(module => ({ default: module.BillingDetailedReport }))
)
const AddDeviceForm = lazy(() =>
  import('../src/device/index.web').then(module => ({ default: module.AddDeviceForm }))
)
const AssignDeviceForm = lazy(() =>
  import('../src/device/index.web').then(module => ({ default: module.AssignDeviceForm }))
)
const EditDevice = lazy(() =>
  import('../src/device/index.web').then(module => ({ default: module.EditDevice }))
)
const ViewDevices = lazy(() =>
  import('../src/device/index.web').then(module => ({ default: module.ViewDevices }))
)
const ViewDevice = lazy(() =>
  import('../src/device/index.web').then(module => ({ default: module.ViewDevice }))
)
const UserDetailView = lazy(() =>
  import('../src/users/index.web').then(module => ({ default: module.UserDetailView }))
)
const EditUserView = lazy(() =>
  import('../src/users/index.web').then(module => ({ default: module.EditUserView }))
)
const UserInviteView = lazy(() =>
  import('../src/users/index.web').then(module => ({ default: module.UserInviteView }))
)
const UsersListView = lazy(() =>
  import('../src/users/index.web').then(module => ({ default: module.UsersListView }))
)
const CreateOrganizationView = lazy(() =>
  import('../src/organizations/index.web').then(module => ({ default: module.CreateOrganizationView }))
)
const OrganizationsListView = lazy(() =>
  import('../src/organizations/index.web').then(module => ({ default: module.OrganizationsListView }))
)
const ViewOrganization = lazy(() =>
  import('../src/organizations/index.web').then(module => ({ default: module.ViewOrganization }))
)
const SignupView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.SignupView }))
)
const LoginView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.LoginView }))
)
const ForgotPasswordView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.ForgotPasswordView }))
)
const SetNewPasswordView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.SetNewPasswordView }))
)
const ChangePasswordView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.ChangePasswordView }))
)
const PrivateRoute = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.PrivateRoute }))
)
const AcceptInviteView = lazy(() =>
  import('../src/auth/index.web').then(module => ({ default: module.AcceptInviteView }))
)

const Main: React.FC = () => {
  const token = useSelector<IAppState, string>(
    (state: IAppState) => state.login.token
  )

  const isSuperUser = useSelector<IAppState, boolean>(state =>
    getIsSuperUser(state)!
  )

  return (
    <Suspense fallback={<div className="lazy-loader"><em className="fas fa-circle-notch fa-spin fa-2x text-muted" /></div>}>
      <Switch>
        <PrivateRoute
          token={token}
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          path="/assign-device"
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible footerVisible={false}>
              <AssignDeviceForm {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/add-device"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible footerVisible={false}>
              <AddDeviceForm {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/devices/:deviceId/edit"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible footerVisible={false}>
              <EditDevice {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/devices/:id"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <ViewDevice {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/devices"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible footerVisible={false}>
              <ViewDevices {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/signup"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={false}>
              <SignupView {...props} />
            </DefaultLayout>
          ))}
        />
        <Route
          path="/login"
          exact
          component={(props: RouteComponentProps) => <LoginView {...props} />}
        />
        <Route
          exact
          path="/forgot-password"
          component={(props: RouteComponentProps) => (
            <ForgotPasswordView {...props} />
          )}
        />
        <Route
          path="/forgot-password/:token"
          component={(props: RouteComponentProps) => (
            <SetNewPasswordView {...props} />
          )}
        />
        <Route
          path="/accept-invite/:inviteToken"
          component={withTracker((props: RouteComponentProps) => (
            <AcceptInviteView {...props} />
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/change-password"
          permissions={
            {
              suAccess: true,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <ChangePasswordView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/profile/edit"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <EditUserView {...props} />
            </DefaultLayout>
          ))}
        />{' '}
        <PrivateRoute
          exact
          token={token}
          path="/profile"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <UserDetailView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/users/:id/edit"
          permissions={
            {
              suAccess: true,
              userAccess: false,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <EditUserView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/users/:id"
          permissions={
            {
              suAccess: true,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <UserDetailView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          permissions={
            {
              suAccess: true,
              userAccess: true,
              adminAccess: true
            }
          }
          path="/users"
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <UsersListView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/invite-user"
          permissions={
            {
              suAccess: true,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <UserInviteView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/practices/:id"
          permissions={
            {
              suAccess: true,
              userAccess: true,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <ViewOrganization {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/practices/:id/edit"
          permissions={
            {
              suAccess: true,
              userAccess: false,
              adminAccess: true
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <CreateOrganizationView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/practices"
          permissions={
            {
              suAccess: true,
              userAccess: false,
              adminAccess: false
            }
          }
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <OrganizationsListView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/add-practice"
          permissions={
            {
              suAccess: true,
              userAccess: false,
              adminAccess: false
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <CreateOrganizationView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/patients"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <AdherenceListView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/add-patient"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible={true} headerVisible>
              <AddPatientView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/patient/:patientId/:panelId"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible>
              <PatientDetailView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/patients/:patientId/edit"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible>
              <EditPatientView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/patient/:patientId"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout sideBarVisible headerVisible>
              <PatientDetailView {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/messages"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout headerVisible sideBarVisible footerVisible>
              <MessagesListView {...props} patientId={2}/>
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          path="/detailed-reports"
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout headerVisible sideBarVisible footerVisible>
              <BillingDetailedReport {...props} />
            </DefaultLayout>
          ))}
        />
        <PrivateRoute
          exact
          token={token}
          path="/billings"
          permissions={
            {
              suAccess: false,
              userAccess: true,
              adminAccess: true
            }}
          CustomComp={withTracker((props: RouteComponentProps) => (
            <DefaultLayout headerVisible sideBarVisible footerVisible>
              <BillingReport {...props} />
            </DefaultLayout>
          ))}
        />
        <Redirect from="/" to={isSuperUser ? `/practices` : '/patients'}/>
      </Switch>
    </Suspense>
  )
}

export default Main
